import { InputBlockBase } from "./inputBlock.js";
import { ConnectionPointType } from "../connection/connectionPointType.js";
/**
 * Determines which generic type of InputBlock we are trying to serialize and calls the appropriate function
 * to serialize the specifics for that type of InputBlock
 * @param inputBlock - The InputBlock to serialize
 * @returns Serialized data for the InputBlock
 */
function SerializeInputBlockData(inputBlock) {
    switch (inputBlock.type) {
        case ConnectionPointType.Texture:
            return SerializeTextureInputBlock(inputBlock);
        case ConnectionPointType.Boolean:
            return SerializeBooleanInputBlock(inputBlock);
        case ConnectionPointType.Float:
            return SerializeFloatInputBlock(inputBlock);
        case ConnectionPointType.Color3:
            return SerializeColor3InputBlock(inputBlock);
        case ConnectionPointType.Color4:
            return SerializeColor4InputBlock(inputBlock);
        case ConnectionPointType.Vector2:
            return SerializeVector2InputBlock(inputBlock);
    }
}
/**
 * Generates the serialized data for a Texture InputBlock
 * @param inputBlock - The Texture InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeTextureInputBlock(inputBlock) {
    const internalTexture = inputBlock.runtimeValue.value?.getInternalTexture();
    const forcedExtension = internalTexture?._extension ?? null;
    let url = internalTexture?.url ?? null;
    if (url === "" || !url) {
        url = inputBlock.editorData?.url ?? null;
    }
    return {
        inputType: ConnectionPointType.Texture,
        url,
        urlTypeHint: inputBlock.editorData?.urlTypeHint ?? null,
        flipY: internalTexture?.invertY ?? null,
        anisotropicFilteringLevel: internalTexture?.anisotropicFilteringLevel ?? null,
        forcedExtension: forcedExtension !== "" ? forcedExtension : null,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * Generates the serialized data for a Boolean InputBlock
 * @param inputBlock - The Boolean InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeBooleanInputBlock(inputBlock) {
    return {
        inputType: ConnectionPointType.Boolean,
        value: inputBlock.runtimeValue.value,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * Generates the serialized data for a Float InputBlock
 * @param inputBlock - The Float InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeFloatInputBlock(inputBlock) {
    return {
        inputType: ConnectionPointType.Float,
        value: inputBlock.runtimeValue.value,
        animationType: inputBlock.editorData?.animationType ?? null,
        valueDeltaPerMs: inputBlock.editorData?.valueDeltaPerMs ?? null,
        min: inputBlock.editorData?.min ?? null,
        max: inputBlock.editorData?.max ?? null,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * Generates the serialized data for a Color3 InputBlock
 * @param inputBlock - The Color3 InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeColor3InputBlock(inputBlock) {
    return {
        inputType: ConnectionPointType.Color3,
        value: inputBlock.runtimeValue.value,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * Generates the serialized data for a Color4 InputBlock
 * @param inputBlock - The Color4 InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeColor4InputBlock(inputBlock) {
    return {
        inputType: ConnectionPointType.Color4,
        value: inputBlock.runtimeValue.value,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * Generates the serialized data for a Vector2 InputBlock
 * @param inputBlock - The Vector2 InputBlock to serialize
 * @returns The serialized data for the InputBlock
 */
function SerializeVector2InputBlock(inputBlock) {
    return {
        inputType: ConnectionPointType.Vector2,
        value: inputBlock.runtimeValue.value,
        appMetadata: inputBlock.appMetadata,
    };
}
/**
 * The V1 serializer for an InputBlock
 */
export const InputBlockSerializer = {
    blockType: InputBlockBase.ClassName,
    serialize: (block) => {
        if (block.blockType !== InputBlockBase.ClassName) {
            throw new Error("Was asked to serialize an unrecognized block type");
        }
        return {
            name: block.name,
            uniqueId: block.uniqueId,
            blockType: InputBlockBase.ClassName,
            namespace: null,
            comments: block.comments,
            data: SerializeInputBlockData(block),
        };
    },
};
//# sourceMappingURL=inputBlock.serializer.js.map